// Smart Daara - Physical school: teachers (profiles, competences, availability)
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = { email: '', password: '', first_name: '', last_name: '', phone: '', subject_ids: [], max_weekly_hours: 20 };

export default function PhysicalTeachers() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [t, s] = await Promise.all([physicalAPI.listTeachers(), physicalAPI.listSubjects()]);
    setTeachers(t.data); setSubjects(s.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const toggleSubject = (id) => setForm({
    ...form,
    subject_ids: form.subject_ids.includes(id) ? form.subject_ids.filter((x) => x !== id) : [...form.subject_ids, id],
  });

  const save = async () => {
    try {
      if (editId) {
        await physicalAPI.updateTeacher(editId, {
          first_name: form.first_name, last_name: form.last_name, phone: form.phone,
          subject_ids: form.subject_ids, max_weekly_hours: form.max_weekly_hours,
        });
        toast.success(fr ? 'Enseignant mis à jour' : 'Teacher updated');
      } else {
        if (!form.email || !form.password || !form.first_name) {
          toast.error(fr ? 'Email, mot de passe et prénom requis' : 'Email, password and first name required'); return;
        }
        await physicalAPI.createTeacher({ ...form, availability: [] });
        toast.success(fr ? 'Enseignant créé' : 'Teacher created');
      }
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openEdit = (t) => {
    setEditId(t.user_id);
    setForm({ ...EMPTY, first_name: t.first_name, last_name: t.last_name, phone: t.phone || '', subject_ids: t.subject_ids || [], max_weekly_hours: t.max_weekly_hours });
    setOpen(true);
  };

  const subjName = (id) => subjects.find((s) => s.id === id)?.name || '?';

  return (
    <div className="space-y-6" data-testid="physical-teachers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-outfit font-bold text-slate-900">{fr ? 'Enseignants' : 'Teachers'}</h1>
        <Button className="bg-emerald-700" onClick={() => { setEditId(null); setForm(EMPTY); setOpen(true); }} data-testid="add-teacher-btn">
          <UserPlus className="w-4 h-4 mr-2" />{fr ? 'Nouvel enseignant' : 'New teacher'}
        </Button>
      </div>

      <div className="space-y-2">
        {teachers.map((t) => (
          <Card key={t.user_id} className="border-0 shadow-sm">
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium">{t.first_name} {t.last_name} <span className="text-slate-400 text-sm">({t.email})</span></p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(t.subject_ids || []).map((id) => <Badge key={id} variant="outline" className="text-xs">{subjName(id)}</Badge>)}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={t.assigned_hours > t.max_weekly_hours ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>
                  {t.assigned_hours}h / {t.max_weekly_hours}h
                </Badge>
                <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-teacher-${t.user_id}`}>{fr ? 'Modifier' : 'Edit'}</Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {teachers.length === 0 && <p className="text-slate-400 text-sm text-center py-6">{fr ? 'Aucun enseignant' : 'No teachers'}</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? (fr ? 'Modifier l\'enseignant' : 'Edit teacher') : (fr ? 'Nouvel enseignant' : 'New teacher')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editId && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="teacher-email-input" /></div>
                <div><Label>{fr ? 'Mot de passe' : 'Password'}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="teacher-password-input" /></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{fr ? 'Prénom' : 'First name'}</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="teacher-firstname-input" /></div>
              <div><Label>{fr ? 'Nom' : 'Last name'}</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{fr ? 'Téléphone' : 'Phone'}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>{fr ? 'Heures max / semaine' : 'Max hours / week'}</Label><Input type="number" value={form.max_weekly_hours} onChange={(e) => setForm({ ...form, max_weekly_hours: parseFloat(e.target.value) || 20 })} /></div>
            </div>
            <div>
              <Label>{fr ? 'Matières enseignées' : 'Subjects taught'}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {subjects.map((s) => (
                  <button key={s.id} type="button"
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${form.subject_ids.includes(s.id) ? 'text-white border-transparent' : 'border-slate-200 text-slate-500'}`}
                    style={form.subject_ids.includes(s.id) ? { backgroundColor: s.color } : {}}
                    onClick={() => toggleSubject(s.id)}
                    data-testid={`teacher-subject-${s.id}`}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button className="bg-emerald-700" onClick={save} data-testid="save-teacher-btn">{fr ? 'Enregistrer' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
